import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable, { formatCount } from '../shared/ReportTable';
import ReportPagination from '../shared/ReportPagination';
import ReportExportButtons from '../shared/ReportExportButtons';
import ColumnPicker from '../shared/ColumnPicker';
import useColumnConfig from '../shared/useColumnConfig';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const PAGE_SIZE = 50;

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};
const TODAY = new Date().toISOString().split('T')[0];

const ALL_COLUMNS = [
    { key: 'delivery_partner_name', label: 'Delivery Partner Name' },
    { key: 'delivery_partner_count', label: 'Count', align: 'right', format: formatCount, isCount: true, total: true },
    { key: 'delivery_partner_sales', label: 'Net Sales', isMoney: true, total: true },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.map((c) => c.key);

const initialFilters = {
    startDate: yesterday(),
    endDate: yesterday(),
    property_id: '',
    tenant_id: '',
};

const DeliveryPartnerSales = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions,
        visibleColumns, exportColumns, totalKeys,
    } = useColumnConfig(ALL_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({ propertyId: filters.property_id });
    const { concessionaireId } = lookups;

    // Delivery-partner report only lists F&B tenants.
    const fbTenantOptions = useMemo(
        () => (lookups.tenants || [])
            .filter((t) => t.tenant_category_id?.category_name === 'F&B')
            .map((t) => ({ value: t._id, label: t.tenant_store_name, sub: t.tenant_code })),
        [lookups.tenants],
    );

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'property_id') next.tenant_id = '';
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            start_date: filters.startDate,
            end_date: filters.endDate,
            property_id: filters.property_id,
            tenant_id: filters.tenant_id,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getdeliverypartnersales`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated Delivery Partner Sales report');
        } catch (err) {
            console.error('delivery partner sales report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Delivery Partner Sales report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const totals = useMemo(() => {
        const t = {};
        totalKeys.forEach((k) => {
            if (visibleColumns.some((c) => c.key === k)) {
                t[k] = reportData.reduce((s, i) => s + (Number(i[k]) || 0), 0);
            }
        });
        return Object.keys(t).length ? t : null;
    }, [reportData, visibleColumns, totalKeys]);

    const fields = [
        { key: 'startDate', label: t('Start date'), type: 'date', role: 'startDate', max: TODAY },
        { key: 'endDate', label: t('End date'), type: 'date', role: 'endDate', max: TODAY },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'tenant_id', label: t('Tenant'), type: 'select', options: fbTenantOptions, placeholder: t('All F&B tenants') },
    ];

    const exportMetadata = useMemo(() => {
        const dates = filters.startDate === filters.endDate
            ? [[t('Date'), filters.startDate]]
            : [[t('From'), filters.startDate], [t('To'), filters.endDate]];
        return [
            ...dates,
            [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
            [t('Tenant'), nameOf(fbTenantOptions, filters.tenant_id) || t('All')],
            [t('Exported By'), login_name],
            [t('Exported On'), new Date().toLocaleString()],
        ];
    }, [filters, lookups, fbTenantOptions, login_name, t]);

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Delivery Partner Sales Report')}
                subtitle={t('Order counts and net sales by delivery partner for F&B tenants.')}
                actions={
                    <ReportExportButtons
                        heading={t('Sales Report-Delivery Partner')}
                        metadata={exportMetadata}
                        columns={exportColumns}
                        data={sortedData}
                        fileName="SalesReportDeliveryPartner"
                        onAfterExport={(fmt) => insertToLog(` exported Delivery Partner Sales report to ${fmt}`)}
                    />
                }
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions}
                    value={selectedColumns}
                    total={ALL_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={visibleColumns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    totals={totals}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No delivery partner sales found for the selected filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('partners')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default DeliveryPartnerSales;
